import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Spinner, PageSkeleton } from '@daycal/ui';
import { FiEdit2, FiSave, FiX, FiFileText, FiExternalLink } from 'react-icons/fi';
import { api } from '../../lib/apiClient.js';
import { LEGAL_PAGES, LEGAL_DEFAULTS, mergeCopy, humanizeKey } from '../../lib/legalContent.js';
import { BOOKING_BASE_URL } from '../../lib/config.js';

// Super Admin > Content > Legal Pages. Lets the admin edit the centralized text on the
// public Privacy / Terms / Refund / Contact pages. Each card shows the current copy (built-in
// defaults merged with any saved overrides) with a pencil to switch into an inline editor.
// Saving stores the full edited copy as an override; the design of the public pages is
// untouched. Anchor `id` keys (TOC/section anchors) are intentionally not editable.

// Immutably set a nested value by path (array of keys/indices) on a plain object/array tree.
function setPath(obj, path, value) {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  clone[head] = setPath(obj[head], rest, value);
  return clone;
}

// Recursively render the copy tree — read-only text, or textareas when editing.
function Fields({ value, editing, path, onChange }) {
  return (
    <div className="space-y-3">
      {Object.entries(value).map(([key, v]) => {
        if (key === 'id') return null; // section anchor — must stay stable, not editable
        const label = humanizeKey(key);
        const childPath = [...path, key];

        if (typeof v === 'string') {
          return (
            <div key={key}>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {label}
              </label>
              {editing ? (
                <textarea
                  value={v}
                  onChange={(e) => onChange(childPath, e.target.value)}
                  rows={v.length > 90 ? 3 : 1}
                  className="w-full resize-y rounded-dc-sm border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              ) : (
                <p className="whitespace-pre-wrap text-sm text-slate-800">{v || <span className="text-slate-300">—</span>}</p>
              )}
            </div>
          );
        }

        if (Array.isArray(v)) {
          return (
            <div key={key}>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {label}
              </label>
              <div className="space-y-2 border-l-2 border-slate-100 pl-3">
                {v.map((item, i) => (
                  <div key={i} className="rounded-dc-sm bg-slate-50 p-3">
                    {typeof item === 'string' ? (
                      editing ? (
                        <textarea
                          value={item}
                          onChange={(e) => onChange([...childPath, i], e.target.value)}
                          rows={item.length > 90 ? 3 : 1}
                          className="w-full resize-y rounded-dc-sm border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                      ) : (
                        <p className="whitespace-pre-wrap text-sm text-slate-800">{item}</p>
                      )
                    ) : (
                      <Fields value={item} editing={editing} path={[...childPath, i]} onChange={onChange} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        // Nested object
        return (
          <div key={key}>
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {label}
            </label>
            <div className="border-l-2 border-slate-100 pl-3">
              <Fields value={v} editing={editing} path={childPath} onChange={onChange} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PageCard({ page, override }) {
  const qc = useQueryClient();
  const defaults = LEGAL_DEFAULTS[page.slug];
  // Current copy = built-in defaults merged with any saved override.
  const current = {
    en: mergeCopy(defaults.en, override?.en),
    bn: mergeCopy(defaults.bn, override?.bn),
  };

  const [editing, setEditing] = useState(false);
  const [locale, setLocale] = useState('en');
  const [draft, setDraft] = useState(current);
  const [msg, setMsg] = useState('');

  const startEdit = () => {
    setDraft(current);
    setMsg('');
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setMsg('');
  };

  const save = useMutation({
    mutationFn: () => api.adminUpdateLegalPage(page.slug, draft),
    onSuccess: () => {
      setMsg('Saved');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['admin-legal-pages'] });
    },
    onError: (err) => setMsg(err.message),
  });

  const onChange = (path, val) =>
    setDraft((d) => ({ ...d, [locale]: setPath(d[locale], path, val) }));

  const shown = editing ? draft[locale] : current[locale];

  return (
    <Card className="mb-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-dc-sm bg-brand-50 text-brand-600">
            <FiFileText className="h-4.5 w-4.5" />
          </span>
          <div>
            <h2 className="text-base font-semibold text-slate-900">{page.label}</h2>
            <a
              href={`${BOOKING_BASE_URL}${page.path}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-brand-600"
            >
              {page.path} <FiExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        {!editing ? (
          <button
            type="button"
            onClick={startEdit}
            className="inline-flex items-center gap-1.5 rounded-dc-sm border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
          >
            <FiEdit2 className="h-3.5 w-3.5" /> Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <Button onClick={() => save.mutate()} disabled={save.isPending} className="!px-3 !py-1.5 text-xs">
              {save.isPending ? <Spinner /> : (<span className="inline-flex items-center gap-1.5"><FiSave className="h-3.5 w-3.5" /> Save</span>)}
            </Button>
            <button
              type="button"
              onClick={cancel}
              className="inline-flex items-center gap-1.5 rounded-dc-sm border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-50"
            >
              <FiX className="h-3.5 w-3.5" /> Cancel
            </button>
          </div>
        )}
      </div>

      {/* Language tabs */}
      <div className="mb-4 inline-flex rounded-full bg-slate-100 p-0.5 text-xs font-semibold">
        {[['en', 'English'], ['bn', 'বাংলা']].map(([code, name]) => (
          <button
            key={code}
            type="button"
            onClick={() => setLocale(code)}
            className={`rounded-full px-3 py-1 transition-colors ${
              locale === code ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            {name}
          </button>
        ))}
      </div>

      <Fields value={shown} editing={editing} path={[]} onChange={onChange} />

      {msg && (
        <p className={`mt-3 text-xs ${save.isError ? 'text-rose-600' : 'text-emerald-600'}`}>{msg}</p>
      )}
    </Card>
  );
}

export default function AdminLegalPages() {
  const { data, isLoading } = useQuery({ queryKey: ['admin-legal-pages'], queryFn: api.adminListLegalPages });
  const [activeSlug, setActiveSlug] = useState(LEGAL_PAGES[0].slug);

  if (isLoading) return <PageSkeleton />;
  const overrides = data?.pages || {};
  const activePage = LEGAL_PAGES.find((p) => p.slug === activeSlug) || LEGAL_PAGES[0];

  return (
    <div className="max-w-3xl">
      <h1 className="mb-1 text-2xl font-semibold">Legal &amp; Info Pages</h1>
      <p className="mb-5 text-sm text-slate-500">
        Edit the text shown on the public Privacy, Terms, Cancellation &amp; Refund and Contact pages.
        Changes go live immediately; the page layout and design are unchanged.
      </p>

      {/* Page tabs — pick one page to edit at a time */}
      <div className="mb-5 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {LEGAL_PAGES.map((p) => (
          <button
            key={p.slug}
            type="button"
            onClick={() => setActiveSlug(p.slug)}
            className={`inline-flex items-center gap-1.5 rounded-dc-sm px-3.5 py-2 text-sm font-semibold transition-colors ${
              p.slug === activeSlug
                ? 'bg-slate-900 text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
            }`}
          >
            <FiFileText className="h-4 w-4" /> {p.label}
          </button>
        ))}
      </div>

      {/* Only the selected page's editor is shown. Remount per tab (key) so switching
          starts clean and reflects the latest saved content. */}
      <PageCard key={activePage.slug} page={activePage} override={overrides[activePage.slug]} />
    </div>
  );
}
